/** Dedicated search view — Home -> criteria -> results (User-Brief 22.09.2026).
 * All criteria optional; "Show results" always works; sticky CTA on mobile. */
import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { ArrowLeft, Search, X } from "lucide-react";
import { getPublicFeed, getPublicFilterOptions } from "../api/publicApi";
import AutoSearchForm from "../components/search/AutoSearchForm";
import ImmoSearchForm from "../components/search/ImmoSearchForm";
import { activeCriteria, paramsToQuery, queryToParams, removeCriterion } from "../lib/searchQuery";

const draftKey = (v) => `da_search_draft_${v}`;

function loadDraft(vertical) {
  try {
    const raw = sessionStorage.getItem(draftKey(vertical));
    return raw ? JSON.parse(raw) : {};
  } catch {
    return {};
  }
}

export default function SearchPage() {
  const { vertical: vParam } = useParams();
  const vertical = vParam === "auto" ? "auto" : "immo";
  const { t } = useTranslation(["discover", "enums"]);
  const navigate = useNavigate();
  const location = useLocation();

  // Init: explicit URL criteria (from "edit filters") > per-vertical draft.
  const [params, setParams] = useState(() => {
    const fromUrl = queryToParams(location.search);
    return Object.keys(fromUrl).length ? fromUrl : loadDraft(vertical);
  });

  // Auto/immo drafts stay separate — switching verticals never mixes state.
  useEffect(() => {
    const fromUrl = queryToParams(location.search);
    setParams(Object.keys(fromUrl).length ? fromUrl : loadDraft(vertical));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [vertical]);

  useEffect(() => {
    try {
      sessionStorage.setItem(draftKey(vertical), JSON.stringify(params));
    } catch { /* private mode */ }
  }, [params, vertical]);

  const set = (partial) => {
    setParams((prev) => {
      const next = { ...prev };
      Object.entries(partial).forEach(([k, v]) => {
        if (v === null || v === undefined || v === "" || v === false) delete next[k];
        else next[k] = v;
      });
      return next;
    });
  };

  const foQ = useQuery({
    queryKey: ["public-filter-options", vertical, params.country || null, params.quality || "all"],
    queryFn: () => getPublicFilterOptions(vertical, {
      country: params.country || undefined,
      quality: params.quality || "all",
    }),
    staleTime: 300_000,
  });
  // Models for EVERY selected make (backend takes one make per call).
  // The query key carries make+country+quality, so options of another
  // country/make combination can never be shown or win a race.
  const selectedMakes = useMemo(
    () => (params.make ? String(params.make).split(",").filter(Boolean).slice(0, 5) : []),
    [params.make],
  );
  const makesKey = useMemo(() => [...selectedMakes].sort().join(","), [selectedMakes]);
  const modelsQ = useQuery({
    queryKey: ["public-models", makesKey, params.country || null, params.quality || "all"],
    enabled: vertical === "auto" && selectedMakes.length > 0,
    queryFn: async () => {
      const rows = await Promise.all(selectedMakes.map(async (m) => ({
        make: m,
        items: (await getPublicFilterOptions("auto", {
          make: m,
          country: params.country || undefined,
          quality: params.quality || "all",
        })).models || [],
      })));
      const byMake = {};
      const options = [];
      const seen = new Set();
      rows.forEach(({ make, items }) => {
        byMake[make] = items.map((it) => it.key || it.value).filter(Boolean);
        items.forEach((it) => {
          const v = it.key || it.value;
          if (!v || seen.has(v)) return;
          seen.add(v);
          options.push({ value: v, label: `${make} · ${it.value} (${it.count})`, make, count: it.count });
        });
      });
      return { options, byMake };
    },
    staleTime: 300_000,
  });

  // LIVE count that honours the ACTUAL criteria — otherwise show none.
  const countParams = useMemo(() => {
    const p = { limit: 1 };
    Object.entries(params).forEach(([k, v]) => {
      if (k !== "location_label" && v !== null && v !== "" && v !== undefined) p[k] = v;
    });
    return p;
  }, [params]);
  const countQ = useQuery({
    queryKey: ["public-feed-count", vertical, countParams],
    queryFn: () => getPublicFeed(vertical, countParams),
    staleTime: 30_000,
  });
  const meta = countQ.data?.meta;
  // Live-Abnahme 06.10.2026: `total_matches` darf `null` sein (Zähler
  // lief in die Zeitgrenze). `|| 0` hätte daraus „0 Treffer" gemacht,
  // obwohl Treffer vorhanden sind — dann lieber keine Zahl nennen.
  const countLabel = (!meta || meta.total_unavailable)
    ? null
    : (meta.total_capped
      ? "2.000+"
      : (meta.total_matches ?? 0).toLocaleString("de-AT"));

  const foStatus = { loading: foQ.isLoading, error: foQ.isError, retry: () => foQ.refetch() };
  const modelsStatus = { loading: modelsQ.isLoading, error: modelsQ.isError, retry: () => modelsQ.refetch() };

  const chips = activeCriteria(vertical, params, t);
  // Typed-but-not-picked location: never a filter, and the search is
  // blocked until the user picked a suggestion or cleared the input.
  const [locText, setLocText] = useState("");
  const locBlocked = locText.trim().length > 0;
  const submit = () => {
    if (locBlocked) return;
    navigate(`/search/${vertical}/results?${paramsToQuery(params)}`);
  };

  return (
    <div className="min-h-dvh bg-zinc-950 text-zinc-100" data-testid={`search-page-${vertical}`}>
      <div className="mx-auto max-w-5xl px-3 pb-28 pt-3 sm:px-6 sm:pt-6 md:pb-10">
        <div className="mb-4 flex items-center gap-2">
          <button
            type="button"
            data-testid="search-back-home"
            onClick={() => navigate("/")}
            className="rounded-lg p-2 text-zinc-400 hover:bg-zinc-900 hover:text-zinc-200"
            aria-label={t("search.backHome")}
          >
            <ArrowLeft size={18} aria-hidden />
          </button>
          <h1 className="text-lg font-bold sm:text-xl">
            {vertical === "auto" ? t("search.titleAuto") : t("search.titleImmo")}
          </h1>
        </div>

        {/* Country quick chips */}
        <div className="mb-4 flex flex-wrap gap-2">
          {[null, "AT", "HR", "DE"].map((c) => (
            <button
              key={c || "all"}
              type="button"
              data-testid={`search-country-${c || "all"}`}
              onClick={() => {
                setLocText("");
                set({ country: c, city: null, postal_code: null, region: null, location_label: null });
              }}
              className={`rounded-full border px-3 py-1.5 text-xs font-medium ${
                (params.country || null) === c
                  ? "border-emerald-500/70 bg-emerald-500/15 text-emerald-300"
                  : "border-zinc-700 text-zinc-400 hover:border-zinc-500"
              }`}
            >
              {c || t("country.all")}
            </button>
          ))}
        </div>

        {vertical === "auto" ? (
          <AutoSearchForm
            params={params}
            set={set}
            fo={foQ.data || {}}
            models={modelsQ.data?.options || []}
            modelsByMake={modelsQ.data?.byMake || {}}
            t={t}
            foStatus={foStatus}
            modelsStatus={modelsStatus}
            locText={locText}
            setLocText={setLocText}
          />
        ) : (
          <ImmoSearchForm
            params={params}
            set={set}
            fo={foQ.data || {}}
            t={t}
            foStatus={foStatus}
            locText={locText}
            setLocText={setLocText}
          />
        )}
        {foQ.isError && (
          <div className="mt-3 flex items-center justify-between gap-3 rounded-xl border border-amber-500/40 bg-amber-500/10 px-3 py-2 text-xs text-amber-200" data-testid="search-options-error">
            <span>{t("search.optionsErrorKeep")}</span>
            <button type="button" data-testid="search-options-retry" onClick={() => foQ.refetch()} className="rounded-md border border-amber-400/50 px-2 py-1 font-medium hover:bg-amber-500/20">
              {t("search.retry")}
            </button>
          </div>
        )}

        {/* Selected criteria summary */}
        {chips.length > 0 && (
          <div className="mt-4 flex flex-wrap items-center gap-1.5" data-testid="search-active-chips">
            {chips.map((c) => (
              <span key={c.key} className="inline-flex items-center gap-1 rounded-full bg-zinc-800/80 px-2.5 py-1 text-[11px] text-zinc-300">
                {c.label}
                <button type="button" aria-label="remove" onClick={() => setParams(removeCriterion(params, c.key))} className="text-zinc-500 hover:text-zinc-200">
                  <X size={11} aria-hidden />
                </button>
              </span>
            ))}
            <button
              type="button"
              data-testid="search-reset-all"
              onClick={() => setParams({})}
              className="text-[11px] text-zinc-500 underline-offset-2 hover:text-zinc-300 hover:underline"
            >
              {t("search.resetAll")}
            </button>
          </div>
        )}

        {/* Desktop submit */}
        <div className="mt-5 hidden md:block">
          {locBlocked && (
            <div className="mb-2 flex items-center gap-2 rounded-xl border border-amber-500/40 bg-amber-500/10 px-3 py-2 text-xs text-amber-200" data-testid="search-submit-blocked">
              <span>{t("search.locationBlocked")}</span>
              <button
                type="button"
                data-testid="search-location-clear-blocked"
                onClick={() => setLocText("")}
                className="ml-auto rounded-md border border-amber-400/50 px-2 py-1 font-medium hover:bg-amber-500/20"
              >
                {t("search.locationClearInput")}
              </button>
            </div>
          )}
          <button
            type="button"
            data-testid="search-submit"
            onClick={submit}
            disabled={locBlocked}
            aria-disabled={locBlocked}
            className={`inline-flex items-center gap-2 rounded-xl px-6 py-3 text-sm font-bold text-zinc-950 ${
              locBlocked ? "cursor-not-allowed bg-emerald-500/40" : "bg-emerald-500 hover:bg-emerald-400"
            }`}
          >
            <Search size={16} aria-hidden />
            {countLabel ? t("search.showResultsN", { count: countLabel }) : t("search.showResults")}
          </button>
        </div>
      </div>

      {/* Mobile: sticky, always reachable — offset by the consent banner */}
      <div
        style={{ bottom: "var(--consent-h, 0px)" }}
        className="fixed inset-x-0 z-30 border-t border-zinc-800 bg-zinc-950/95 p-3 backdrop-blur md:hidden"
      >
        {locBlocked && (
          <div className="mb-2 flex items-center gap-2 rounded-lg border border-amber-500/40 bg-amber-500/10 px-2.5 py-1.5 text-[11px] text-amber-200" data-testid="search-submit-blocked-mobile">
            <span className="flex-1">{t("search.locationPickHint")}</span>
            <button
              type="button"
              data-testid="search-location-clear-blocked-mobile"
              onClick={() => setLocText("")}
              className="rounded-md border border-amber-400/50 px-2 py-0.5 font-medium"
            >
              {t("search.locationClearInput")}
            </button>
          </div>
        )}
        <button
          type="button"
          data-testid="search-submit-mobile"
          onClick={submit}
          disabled={locBlocked}
          aria-disabled={locBlocked}
          className={`flex w-full items-center justify-center gap-2 rounded-xl px-6 py-3.5 text-sm font-bold text-zinc-950 ${
            locBlocked ? "cursor-not-allowed bg-emerald-500/40" : "bg-emerald-500"
          }`}
        >
          <Search size={16} aria-hidden />
          {countLabel ? t("search.showResultsN", { count: countLabel }) : t("search.showResults")}
        </button>
      </div>
    </div>
  );
}
